/**
 * StatusChangeCommentModal — a small "reason for this status change" popup.
 *
 * Extracted from `ApplicationStatusDropdown.tsx`'s inline mandatory-comment
 * modal (AC3/AC5) so a second trigger point (the TalentOS match report's
 * Approve/Deny butons) can show the exact same popup instead of a new one.
 * `ApplicationStatusDropdown.tsx` itself still owns its original inline copy
 * for now — deliberately left untouched (smallest safe change) rather than
 * risking its accordion/rounds logic on an unrelated refactor.
 *
 * The comment, once submitted, reaches the backend's
 * `PATCH /api/v2/matching/jobs/{jobId}/candidates/{resumeId}/application-status`
 * via the `onConfirm` callback the host supplies — that endpoint writes it to
 * `assignment_audit_log.change_reason`, so anything typed here is on the
 * audit trail.
 */
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { getStageColor } from '../../utils/stageColors'
import type { CandidateStatusFlat } from '../../types/candidateStatus'

// Same keyword rule as ApplicationStatusDropdown.tsx — kept in sync manually
// since that file's copy is intentionally not imported from here (see above).
const NEGATIVE_KEYWORDS = ['HOLD', 'REJECT', 'WITHDRAWN', 'NO_SHOW', 'DROPOUT', 'DECLINED', 'REVOKED', 'FAILED']
const COMMENT_EXEMPT_CODES = ['SCREEN_REJECTED_BY_CLIENT']

export function isNegativeStatus(code?: string): boolean {
  const c = (code || '').toUpperCase()
  if (COMMENT_EXEMPT_CODES.includes(c)) return false
  return NEGATIVE_KEYWORDS.some((k) => c.includes(k))
}

interface StatusChangeCommentModalProps {
  status: CandidateStatusFlat
  /** Overrides whether the comment is mandatory, regardless of
   *  `isNegativeStatus`. Leave unset to defer to that (the dropdown's
   *  original behavior); pass `true` for a trigger — like Approve — where a
   *  reason should always be captured even for a positive outcome. */
  commentRequired?: boolean
  onCancel: () => void
  /** Called with the trimmed comment. The status id itself is the host's to
   *  know (it chose which status to open this modal for), so it isn't
   *  threaded back through here. */
  onConfirm: (comment: string) => Promise<void>
  /** Fires after a successful `onConfirm` — the host closes/unmounts the
   *  modal here rather than the modal assuming how to do that itself. */
  onSuccess: () => void
}

export default function StatusChangeCommentModal({
  status,
  commentRequired,
  onCancel,
  onConfirm,
  onSuccess,
}: StatusChangeCommentModalProps) {
  const [comment, setComment] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const required = commentRequired ?? isNegativeStatus(status.statusCode)

  const handleConfirm = async () => {
    if (isUpdating) return
    if (required && !comment.trim()) return
    setIsUpdating(true)
    try {
      await onConfirm(comment.trim())
      toast.success(`Status updated to ${status.statusName}`)
      onSuccess()
    } catch (error) {
      console.error('Failed to update status:', error)
      toast.error('Failed to update status. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    if (isUpdating) return
    onCancel()
  }

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" onClick={handleCancel} />
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-sm p-4">
        <div className="text-xs text-gray-500">Update status to</div>
        <div className="flex items-center gap-2 mt-1 mb-3">
          <span className={`w-2 h-2 rounded-full ${getStageColor(status.stageCode).dot}`} />
          <span className="text-sm font-semibold text-gray-900">{status.statusName}</span>
        </div>

        <label className="block text-xs font-medium text-gray-700 mb-1">
          Comment / Reason{' '}
          {required ? (
            <span className="text-red-500">*</span>
          ) : (
            <span className="text-gray-400 font-normal">(optional)</span>
          )}
        </label>
        <textarea
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder={required ? 'Enter the reason for this status change…' : 'Add an optional note…'}
          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-[11px] text-gray-400">{required ? 'Required' : 'Optional'}</span>
          <span className="text-[11px] text-gray-400">{comment.length}/500</span>
        </div>

        <div className="flex items-center justify-end gap-2 mt-3">
          <button
            onClick={handleCancel}
            disabled={isUpdating}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={(required && !comment.trim()) || isUpdating}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isUpdating && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
